/**
 * Centralized artwork file type validation and configuration
 * Used across Design Lab, design-upload-cta, and other upload components
 *
 * CRITICAL: This validation must stay in sync with Constants::PRINT_SUPPORTED_MIME_TYPES
 * in the backend to prevent uploads that pass frontend validation but fail backend validation.
 */

// Accept configuration for HTML file inputs (react-dropzone format) - single source of truth
export const ACCEPT_CONFIG = {
  "image/bmp": [".bmp"],
  "image/gif": [".gif"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/svg+xml": [".svg"],
  "image/tiff": [".tiff", ".tif"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"]
};

// Derived arrays from ACCEPT_CONFIG - no duplication
export const SUPPORTED_IMAGE_MIME_TYPES = Object.keys(ACCEPT_CONFIG).filter(mimeType =>
  mimeType.startsWith("image/")
);

export const SUPPORTED_DOCUMENT_MIME_TYPES = Object.keys(ACCEPT_CONFIG).filter(mimeType =>
  mimeType.startsWith("application/")
);

/**
 * Validates if a file type is supported for artwork upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, message: string }} - { valid: boolean, message: string }
 */
export const validateArtworkFileType = file => {
  // Check supported MIME types (strict whitelist matching backend)
  const isValidMimeType =
    SUPPORTED_IMAGE_MIME_TYPES.includes(file.type) ||
    SUPPORTED_DOCUMENT_MIME_TYPES.includes(file.type);

  const message = isValidMimeType ? "" : "You can't upload files of this type";
  return { valid: isValidMimeType, message };
};

/**
 * Get accept string for HTML file input
 * @returns {string} - Comma-separated accept string
 */
export const getAcceptString = () => {
  const acceptArray = [];

  for (const [mimeType, extensions] of Object.entries(ACCEPT_CONFIG)) {
    acceptArray.push(mimeType);
    acceptArray.push(...extensions);
  }

  return acceptArray.join(",");
};

export const DESIGN_LAB_ACCEPT_CONFIG = {
  "image/bmp": [".bmp"],
  "image/gif": [".gif"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/svg+xml": [".svg"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "image/tiff": [".tiff", ".tif"]
};

export const DESIGN_LAB_PREVIEW_BASED_EXTENSIONS = /^image\/(png|jpeg|jpg)$/;

// Derived arrays from DESIGN_LAB_ACCEPT_CONFIG
export const DESIGN_LAB_SUPPORTED_IMAGE_MIME_TYPES = Object.keys(DESIGN_LAB_ACCEPT_CONFIG).filter(
  mimeType => mimeType.startsWith("image/")
);

export const DESIGN_LAB_SUPPORTED_DOCUMENT_MIME_TYPES = Object.keys(
  DESIGN_LAB_ACCEPT_CONFIG
).filter(mimeType => mimeType.startsWith("application/"));

/**
 * Validates if a file type is supported for design lab artwork upload
 * Excludes PDF, HEIC, and TIFF formats
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, message: string }} - { valid: boolean, message: string }
 */
export const validateDesignLabArtworkFileType = file => {
  // Check supported MIME types for design lab (excludes PDF, HEIC, TIFF)
  const isValidMimeType =
    DESIGN_LAB_SUPPORTED_IMAGE_MIME_TYPES.includes(file.type) ||
    DESIGN_LAB_SUPPORTED_DOCUMENT_MIME_TYPES.includes(file.type);

  const message = isValidMimeType ? "" : "You can't upload files of this type";
  return { valid: isValidMimeType, message };
};

/**
 * Validates image dimensions for raster images (PNG/JPEG)
 * Ensures the largest dimension is at least 200px
 * @param {File} file - The file to validate
 * @returns {Promise<{ valid: boolean, message: string }>} - Promise resolving to { valid: boolean, message: string }
 */
export const validateImageDimensions = file =>
  new Promise(resolve => {
    // Only validate PNG and JPEG files
    if (!file.type.match(DESIGN_LAB_PREVIEW_BASED_EXTENSIONS)) {
      resolve({ valid: true, message: "" });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const largestDimension = Math.max(img.width, img.height);
      const isValid = largestDimension >= 200;

      const message = isValid
        ? ""
        : "Image dimensions are too small. The largest dimension must be at least 200px for optimal print quality.";

      // Revoke the object URL to prevent memory leaks
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: isValid, message });
    };

    img.onerror = () => {
      // If we can't load the image, allow it to pass through to backend validation
      // Still revoke the object URL to prevent memory leaks
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: true, message: "" });
    };

    img.src = objectUrl;
  });

/**
 * Comprehensive validation for design lab artwork files including dimensions
 * @param {File} file - The file to validate
 * @returns {Promise<{ valid: boolean, message: string }>} - Promise resolving to { valid: boolean, message: string }
 */
export const validateDesignLabArtworkFile = async file => {
  // First check file type
  const typeValidation = validateDesignLabArtworkFileType(file);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  // Then check dimensions for raster images
  const dimensionValidation = await validateImageDimensions(file);
  if (!dimensionValidation.valid) {
    return dimensionValidation;
  }

  return { valid: true, message: "" };
};

/**
 * Get design lab specific accept string for HTML file input
 * @returns {string} - Comma-separated accept string for design lab
 */
export const getDesignLabAcceptString = () => {
  const acceptArray = [];

  for (const [mimeType, extensions] of Object.entries(DESIGN_LAB_ACCEPT_CONFIG)) {
    acceptArray.push(mimeType);
    acceptArray.push(...extensions);
  }

  return acceptArray.join(",");
};
